import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import OrderCancelModal from '../components/OrderCancelModal';
import {
  ArrowLeftIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  CreditCardIcon,
  UserIcon,
  MapPinIcon,
  DocumentTextIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';

// Status color and label mapping
const STATUS_CONFIG = {
  DRAFT: {
    label: 'Draft',
    color: 'bg-slate-100 text-slate-800 border-slate-300',
    icon: DocumentDuplicateIcon,
  },
  PENDING: {
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    icon: ClockIcon,
  },
  AWAITING_PAYMENT: {
    label: 'Awaiting Payment',
    color: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: ClockIcon,
  },
  PAYMENT_FAILED: {
    label: 'Payment Failed',
    color: 'bg-red-100 text-red-800 border-red-300',
    icon: XCircleIcon,
  },
  PAID: {
    label: 'Paid',
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: CheckCircleIcon,
  },
  PROCESSING: {
    label: 'Processing',
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    icon: CubeIcon,
  },
  PARTIALLY_SHIPPED: {
    label: 'Partially Shipped',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    icon: TruckIcon,
  },
  SHIPPED: {
    label: 'Shipped',
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    icon: TruckIcon,
  },
  DELIVERED: {
    label: 'Delivered',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    icon: CheckCircleIcon,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-800 border-red-300',
    icon: XCircleIcon,
  },
  REFUNDED: {
    label: 'Refunded',
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: CreditCardIcon,
  },
  DISPUTED: {
    label: 'Disputed',
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    icon: ExclamationTriangleIcon,
  },
  FAILED: {
    label: 'Failed',
    color: 'bg-red-100 text-red-800 border-red-300',
    icon: XCircleIcon,
  },
};

const DEFAULT_STATUS_CONFIG = {
  label: 'Unknown',
  color: 'bg-gray-100 text-gray-800 border-gray-300',
  icon: QuestionMarkCircleIcon,
};

export default function OrderDetail({ orderId: propId, embedded = false }) {
  const params = useParams();
  const navigate = useNavigate();
  const id = propId || params.id;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/auth/payments/orders/${id}`);
      const data = response.data.data || response.data;
      setOrder(data);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.response?.data?.message || 'Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = async (reason) => {
    try {
      setCancelLoading(true);
      await api.post(`/auth/payments/orders/${id}/cancel`, { reason });
      setShowCancelModal(false);
      fetchOrder();
    } catch (err) {
      console.error('Error cancelling order:', err);
      const errorMessage =
        err.response?.data?.message || 'Failed to cancel order';
      alert(errorMessage);
    } finally {
      setCancelLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    if (cents === null || cents === undefined) return '-';
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || DEFAULT_STATUS_CONFIG;
    const Icon = config.icon;
    return (
      <span
        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${config.color}`}
      >
        <Icon className="h-4 w-4 mr-1.5" />
        {config.label}
      </span>
    );
  };

  const canCancelOrder = (order) => {
    return order?.status === 'PENDING';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`space-y-4 ${embedded ? 'p-4' : ''}`}>
        {!embedded && (
          <button
            onClick={() => navigate('/orders')}
            className="inline-flex items-center text-slate-600 hover:text-slate-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Orders
          </button>
        )}
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className={`space-y-4 ${embedded ? 'p-4' : ''}`}>
        {!embedded && (
          <button
            onClick={() => navigate('/orders')}
            className="inline-flex items-center text-slate-600 hover:text-slate-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Orders
          </button>
        )}
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
          Order not found
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${embedded ? 'p-4' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        {!embedded && (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/orders')}
              className="inline-flex items-center text-slate-600 hover:text-slate-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Orders
            </button>
          </div>
        )}
        {canCancelOrder(order) && (
          <button
            onClick={() => setShowCancelModal(true)}
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all hover:shadow-md"
          >
            <XCircleIcon className="h-5 w-5 mr-2" />
            Cancel Order
          </button>
        )}
      </div>

      {/* Order Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <PageHeader
          title={`Order ${order.orderNumber}`}
          description={`ID: ${order.id}`}
          actions={getStatusBadge(order.status)}
        />

        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-slate-500">Order Total</p>
            <p className="text-lg font-bold text-slate-900">
              {formatCurrency(order.totalInCents)}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Created</p>
            <p className="text-sm font-medium text-slate-900">
              {formatDate(order.createdAt)}
            </p>
          </div>
          {order.paidAt && (
            <div>
              <p className="text-sm text-slate-500">Paid</p>
              <p className="text-sm font-medium text-slate-900">
                {formatDate(order.paidAt)}
              </p>
            </div>
          )}
          {order.cancelledAt && (
            <div>
              <p className="text-sm text-slate-500">Cancelled</p>
              <p className="text-sm font-medium text-red-600">
                {formatDate(order.cancelledAt)}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className={`grid gap-4 ${embedded ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'}`}>
        {/* Order Items */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                <CubeIcon className="h-5 w-5 mr-2 text-slate-500" />
                Order Items
              </h2>
            </div>
            <div className="divide-y divide-slate-200">
              {order.items && order.items.length > 0 ? (
                order.items.map((item, index) => (
                  <div key={index} className="px-4 py-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-slate-900">
                          {item.name || item.productName || `Item ${index + 1}`}
                        </h3>
                        {item.description && (
                          <p className="text-sm text-slate-500 mt-1">
                            {item.description}
                          </p>
                        )}
                        {item.sku && (
                          <p className="text-xs text-slate-400 mt-1">
                            SKU: {item.sku}
                          </p>
                        )}
                        <div className="mt-2 flex items-center space-x-4 text-sm">
                          <span className="text-slate-600">
                            Qty: <span className="font-medium">{item.quantity || 1}</span>
                          </span>
                          <span className="text-slate-600">
                            Unit Price:{' '}
                            <span className="font-medium">
                              {formatCurrency(item.priceInCents || item.unitPriceInCents)}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-slate-900">
                          {formatCurrency(
                            (item.priceInCents || item.unitPriceInCents || 0) *
                            (item.quantity || 1)
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-slate-500">
                  <CubeIcon className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                  <p>No items in this order</p>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
              <div className="space-y-2">
                {order.subtotalInCents !== undefined && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-900">
                      {formatCurrency(order.subtotalInCents)}
                    </span>
                  </div>
                )}
                {order.shippingInCents !== undefined && order.shippingInCents > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Shipping</span>
                    <span className="text-slate-900">
                      {formatCurrency(order.shippingInCents)}
                    </span>
                  </div>
                )}
                {order.taxInCents !== undefined && order.taxInCents > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-slate-900">
                      {formatCurrency(order.taxInCents)}
                    </span>
                  </div>
                )}
                {order.discountInCents !== undefined && order.discountInCents > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Discount</span>
                    <span className="text-green-600">
                      -{formatCurrency(order.discountInCents)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-base font-semibold pt-2 border-t border-slate-300">
                  <span className="text-slate-900">Total</span>
                  <span className="text-slate-900">
                    {formatCurrency(order.totalInCents)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Notes / Cancellation Reason */}
          {(order.notes || order.cancellationReason) && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-slate-500" />
                  Notes
                </h2>
              </div>
              <div className="px-4 py-3 space-y-4">
                {order.notes && (
                  <div>
                    <p className="text-sm font-medium text-slate-700">Order Notes</p>
                    <p className="text-sm text-slate-600 mt-1">{order.notes}</p>
                  </div>
                )}
                {order.cancellationReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-700">
                      Cancellation Reason
                    </p>
                    <p className="text-sm text-red-600 mt-1">
                      {order.cancellationReason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-slate-500" />
                Customer
              </h2>
            </div>
            <div className="px-4 py-3 space-y-3">
              {order.customer ? (
                <>
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="text-sm font-medium text-slate-900">
                      {order.customer.name || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="text-sm font-medium text-slate-900">
                      {order.customer.email || '-'}
                    </p>
                  </div>
                  {order.customer.phone && (
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="text-sm font-medium text-slate-900">
                        {order.customer.phone}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">No customer information</p>
              )}
            </div>
          </div>

          {/* Shipping Address */}
          {order.shippingAddress && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                  <MapPinIcon className="h-5 w-5 mr-2 text-slate-500" />
                  Shipping Address
                </h2>
              </div>
              <div className="px-4 py-3">
                <address className="text-sm text-slate-600 not-italic space-y-1">
                  {order.shippingAddress.name && (
                    <p className="font-medium text-slate-900">
                      {order.shippingAddress.name}
                    </p>
                  )}
                  {order.shippingAddress.line1 && <p>{order.shippingAddress.line1}</p>}
                  {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
                  <p>
                    {[
                      order.shippingAddress.city,
                      order.shippingAddress.state,
                      order.shippingAddress.postalCode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {order.shippingAddress.country && (
                    <p>{order.shippingAddress.country}</p>
                  )}
                </address>
              </div>
            </div>
          )}

          {/* Billing Address */}
          {order.billingAddress && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                  <CreditCardIcon className="h-5 w-5 mr-2 text-slate-500" />
                  Billing Address
                </h2>
              </div>
              <div className="px-4 py-3">
                <address className="text-sm text-slate-600 not-italic space-y-1">
                  {order.billingAddress.name && (
                    <p className="font-medium text-slate-900">
                      {order.billingAddress.name}
                    </p>
                  )}
                  {order.billingAddress.line1 && <p>{order.billingAddress.line1}</p>}
                  {order.billingAddress.line2 && <p>{order.billingAddress.line2}</p>}
                  <p>
                    {[
                      order.billingAddress.city,
                      order.billingAddress.state,
                      order.billingAddress.postalCode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {order.billingAddress.country && (
                    <p>{order.billingAddress.country}</p>
                  )}
                </address>
              </div>
            </div>
          )}

          {/* Payment Information */}
          {order.payment && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                  <CreditCardIcon className="h-5 w-5 mr-2 text-slate-500" />
                  Payment
                </h2>
              </div>
              <div className="px-4 py-3 space-y-3">
                {order.payment.method && (
                  <div>
                    <p className="text-sm text-slate-500">Method</p>
                    <p className="text-sm font-medium text-slate-900 capitalize">
                      {order.payment.method}
                    </p>
                  </div>
                )}
                {order.payment.last4 && (
                  <div>
                    <p className="text-sm text-slate-500">Card</p>
                    <p className="text-sm font-medium text-slate-900">
                      •••• {order.payment.last4}
                    </p>
                  </div>
                )}
                {order.payment.transactionId && (
                  <div>
                    <p className="text-sm text-slate-500">Transaction ID</p>
                    <p className="text-xs font-mono text-slate-600 break-all">
                      {order.payment.transactionId}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <OrderCancelModal
          order={order}
          onConfirm={handleCancelConfirm}
          onCancel={() => setShowCancelModal(false)}
          loading={cancelLoading}
        />
      )}
    </div>
  );
}
